import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ScanResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchResults();
  }, [id]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`/api/analysis/${id}`);
      setResults(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreGrade = (score) => {
    if (score >= 90) return 'A+';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B';
    if (score >= 60) return 'C';
    if (score >= 50) return 'D';
    return 'F';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-red-800 font-semibold mb-2">Error Loading Results</h2>
            <p className="text-red-600">{error}</p>
            <button 
              onClick={() => navigate('/')}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AEO Analysis Results
          </h1>
          <p className="text-gray-600">
            Scanned: {results?.url || 'Unknown URL'}
          </p>
        </div>

        {/* Score Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="text-center">
            <div className={`text-6xl font-bold ${getScoreColor(results?.overallScore || 0)} mb-2`}>
              {results?.overallScore || 0}/100
            </div>
            <div className="text-2xl text-gray-700 mb-1">
              Grade: {getScoreGrade(results?.overallScore || 0)}
            </div>
            <p className="text-gray-600">
              {results?.pagesScanned || 0} pages scanned • 
              {results?.issues?.length || results?.topIssues?.length || 0} issues found
            </p>
          </div>
          
          {/* Enhanced Scores */}
          {results?.contentScore && (
            <div className="mt-6 pt-6 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-sm text-gray-500">Content Quality</div>
                <div className={`text-2xl font-bold ${getScoreColor(results.contentScore.overall || 0)}`}>
                  {results.contentScore.overall || 0}/100
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-500">Readability</div>
                <div className="text-2xl font-bold">
                  {results.contentScore?.readability?.interpretation || 'N/A'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-500">Keyword Optimization</div>
                <div className={`text-2xl font-bold ${getScoreColor(results.contentScore?.keyword?.score || 0)}`}>
                  {results.contentScore?.keyword?.score || 0}/100
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Issues Section */}
        {(results?.issues || results?.topIssues) && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Issues Found</h2>
            <div className="space-y-4">
              {(results?.issues || results?.topIssues || []).map((issue, index) => (
                <div key={index} className="border-l-4 border-yellow-400 pl-4 py-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800">
                        {issue.message}
                      </h3>
                      {issue.url && (
                        <p className="text-xs text-gray-500 mt-1">
                          Found on: <span className="font-mono">{issue.url}</span>
                        </p>
                      )}
                      <p className="text-sm text-gray-600 mt-1">
                        {issue.fix}
                      </p>
                      {issue.affectedPages && issue.affectedPages.length > 1 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Affected pages: {issue.affectedPages.length}
                        </p>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      issue.severity === 'high' ? 'bg-red-100 text-red-800' :
                      issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {issue.severity || 'info'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations Section */}
        {results?.recommendations && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Recommendations</h2>
            <div className="space-y-4">
              {results.recommendations.map((rec, index) => (
                <div key={index} className="border-l-4 border-blue-400 pl-4 py-2">
                  <h3 className="font-semibold text-gray-800">{rec.title}</h3>
                  <p className="text-gray-600 mt-1">{rec.description}</p>
                  {rec.impact && (
                    <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      Impact: {rec.impact}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upgrade Message for Free Users */}
        {results?.upgradeMessage && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <p className="text-blue-800">{results.upgradeMessage}</p>
            <button 
              onClick={() => navigate('/pricing')}
              className="mt-3 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Upgrade to Pro
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button 
            onClick={() => navigate('/')}
            className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
          >
            Scan Another Site
          </button>
          {results?.recommendations && (
            <button 
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
              onClick={() => window.print()}
            >
              Export Report
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScanResults;
